import {
  useLocation,
  useNavigate,
  useMatch,
  useParams,
  useSearchParams,
  createBrowserRouter,
  redirect,
  generatePath,
  RouterProvider,
  Navigate,
  Route,
  Outlet,
  NavigateFunction,
} from 'react-router-dom'

import isEmpty from 'lodash/isEmpty'
import { isNil, mapObjIndexed, reject } from 'ramda'
import { getUnixTime } from 'date-fns'
export type SearchParams = { [key: string]: any }

const isNumericKey = (key: string): boolean => /^\d+$/.test(key)

const convertNumericKeysToArrays = (obj: any): any => {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }

  const keys = Object.keys(obj)
  const allNumeric = keys.length > 0 && keys.every(isNumericKey)

  if (allNumeric) {
    const arr: any[] = []
    keys.forEach((key) => {
      arr[parseInt(key, 10)] = convertNumericKeysToArrays(obj[key])
    })
    return arr
  }

  const result: any = {}
  for (const key of keys) {
    result[key] = convertNumericKeysToArrays(obj[key])
  }
  return result
}

export const parseSearchParams = <T extends SearchParams>(
  searchString: string
): T => {
  try {
    const params = new URLSearchParams(searchString)
    const result: any = {}

    for (const [key, val] of params) {
      const targetObj = result
      const keys = key.split(/[[\]]/).filter((k) => k.length > 0)
      const lastKeyIndex = keys.length - 1

      keys.reduce((acc, currentKey, index) => {
        let decodedValue
        try {
          decodedValue = decodeURIComponent(val)
        } catch (error) {
          decodedValue = val
          console.warn(`Failed to decode URL parameter value: ${val}`, error)
        }
        const isArray = key.endsWith('[]') && index === lastKeyIndex

        if (index === lastKeyIndex) {
          if (isArray) {
            if (!acc[currentKey]) {
              acc[currentKey] = [decodedValue]
            } else {
              acc[currentKey].push(decodedValue)
            }
          } else {
            acc[currentKey] = decodedValue
          }
        } else {
          if (!acc[currentKey]) {
            acc[currentKey] = {}
          }
          return acc[currentKey]
        }

        return acc
      }, targetObj)
    }

    return convertNumericKeysToArrays(result) as T
  } catch (e) {
    console.error(e)
    throw e
  }
}

const isArrayOfObjects = (value: any): boolean => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    !(value[0] instanceof Date)
  )
}

export const toSearchParamsString = <T extends SearchParams>(
  params: T,
  prefix = ''
): string => {
  try {
    const parts = mapObjIndexed((value, key) => {
      const fullKey = prefix ? `${prefix}[${key}]` : key
      if (isArrayOfObjects(value)) {
        return (value as any[])
          .map((item, index) => {
            return toSearchParamsString(item, `${fullKey}[${index}]`)
          })
          .filter(Boolean)
          .join('&')
      } else if (Array.isArray(value) && !isEmpty(value)) {
        return value
          .map((val) => {
            if (val instanceof Date) {
              return `${fullKey}[]=${encodeURIComponent(getUnixTime(val))}`
            }
            return `${fullKey}[]=${encodeURIComponent(val)}`
          })
          .join('&')
      } else if (value instanceof Date) {
        return `${fullKey}=${encodeURIComponent(getUnixTime(value))}`
      } else if (
        typeof value === 'object' &&
        value !== null &&
        !isEmpty(value)
      ) {
        return toSearchParamsString(value, fullKey)
      } else if (
        !isNil(value) &&
        value !== '' &&
        !Array.isArray(value) &&
        typeof value !== 'object'
      ) {
        return `${fullKey}=${encodeURIComponent((value as { toString(): string }).toString())}`
      }
      return null
    }, params)

    return reject(isNil, Object.values(parts)).join('&')
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const useObjectFormattedSearchParams = <T extends SearchParams>() => {
  const location = useLocation()
  const navigate = useNavigate()

  const replaceSearchParams = (params: T) => {
    navigate(`${location.pathname}?${toSearchParamsString<T>(params)}`)
  }

  return [parseSearchParams<T>(location.search), replaceSearchParams] as const
}

export type { NavigateFunction }

export {
  useLocation,
  useNavigate,
  useMatch,
  useParams,
  useSearchParams,
  createBrowserRouter,
  redirect,
  generatePath,
  RouterProvider,
  Navigate,
  Route,
  Outlet,
}
